import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'

import { Icon } from '@/common/components/Icon'
import { PageTitle } from '@/components'
import { Button, Input, Modal } from '@/components/ui'
import FormGroup from '@/core/components/FormGroup'
import {
  deleteVendorLocation,
  IVendorLocation,
  updateVendorLocation,
} from '@/core/services/vendors'
import { DeleteConfirmationModal } from '@/home/components/DeleteConfirmationModal'
import { MessageBox } from '@/home/components/messages/MessageBox'

interface EditLocationModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  vendorId: string
  location: IVendorLocation | null
  onSuccess: () => void
}

interface ILocationValues {
  name: string
}

const schema = yup.object().shape({
  name: yup.string().required('Field is required'),
})

export const EditLocationModal = ({
  open,
  onOpenChange,
  vendorId,
  location,
  onSuccess,
}: EditLocationModalProps) => {
  const [deleteModal, setDeleteModal] = useState(false)
  const [isError, setIsError] = useState(false)
  const [showMessageBox, setShowMessageBox] = useState(false)
  const [message, setMessage] = useState('')

  const {
    handleSubmit,
    register,
    formState: { errors, dirtyFields },
    reset,
    setValue,
  } = useForm<ILocationValues>({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: { name: '' },
  })

  useEffect(() => {
    if (!open) return
    if (location) {
      setValue('name', location.name, { shouldValidate: true })
    }
    return () => {
      reset({ name: '' })
    }
  }, [location, open])

  const handleClose = () => {
    onOpenChange(false)
  }

  const submit = async (values: ILocationValues) => {
    if (!location) return
    try {
      const { status } = await updateVendorLocation(vendorId, location.id, {
        name: values.name,
      })
      if (status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Update Successful')
      }
      onSuccess()
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      }
    }
    handleClose()
  }

  const handleDeleteLocation = async () => {
    if (!location) return
    try {
      const { status } = await deleteVendorLocation(vendorId, location.id)
      if (status === 204) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Delete Successful')
      }
      onSuccess()
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      }
    }
    handleClose()
  }

  return (
    <>
      <Modal
        open={open}
        onOpenChange={(o) => {
          if (!o) handleClose()
        }}
        size="xl"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle
              icon="Vendor Entity"
              title={
                <span className="flex items-center gap-2">
                  Edit <span className="text-accent-1">Location</span>
                </span>
              }
            />
          </div>
        }
        footer={
          <div className="flex w-full items-center justify-between gap-2">
            <Button
              onClick={() => setDeleteModal(true)}
              variant="destructive"
              className="mr-auto"
            >
              Delete Location
              <Icon
                icon="close-circle"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
              />
            </Button>
            <div className="flex items-center gap-2">
              <Button onClick={handleClose} type="button" variant="secondary">
                Cancel
              </Button>
              <Button
                disabled={Object.keys(dirtyFields).length === 0}
                form="edit-location-form"
                type="submit"
                variant="primary"
              >
                Save
              </Button>
            </div>
          </div>
        }
      >
        <form
          id="edit-location-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-7"
        >
          <FormGroup label="Name" error={errors?.name?.message}>
            <Input
              isInvalid={!!errors?.name?.message}
              {...register('name')}
              type="text"
              placeholder="Enter Location Name"
            />
          </FormGroup>
        </form>
      </Modal>

      <DeleteConfirmationModal
        showDeleteConfirmationModal={deleteModal}
        setDeleteConfirmationModal={setDeleteModal}
        deleteTitle="Location"
        deleteFunc={handleDeleteLocation}
        deletedItem={location?.name}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
